import { useCallback, useEffect, useRef, useState } from 'react';
import { type KeyedMutator } from 'swr';

import {
    type AdminAgentRunRead,
    type Mode,
} from '@apps/web/components/admin/admin_agent_chat_session_types';
import {
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
} from '@apps/web/components/phoebe_chat_view';
import { type usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { generateUuid } from '@libraries/typescript/utilities';

export function useAdminAgentToolActions({
    allowedSelectedRunId,
    currentMode,
    mutateRun,
    runEventStream,
}: {
    readonly allowedSelectedRunId: string | null;
    // Admin toggle mode for outgoing tool-action requests so
    // interrupt/decision headers stay in the same live-vs-sandbox
    // bucket as the run itself. Callers thread the same value they
    // pass to `useAdminAgentChatSession.currentMode`.
    readonly currentMode: Mode;
    readonly mutateRun: KeyedMutator<AdminAgentRunRead>;
    readonly runEventStream: ReturnType<typeof usePhoebeAgentRunEventStream>;
}) {
    const apiClient = getApiClient();
    const [isInterrupting, setIsInterrupting] = useState(false);
    const [decidingCallIds, setDecidingCallIds] = useState<
        Record<string, boolean>
    >({});
    const [inspectedToolEntryId, setInspectedToolEntryId] = useState<
        string | null
    >(null);
    const pendingToolDecisionOperationIdsRef = useRef<Record<string, string>>(
        {},
    );

    useEffect(() => {
        setDecidingCallIds({});
        pendingToolDecisionOperationIdsRef.current = {};
        setInspectedToolEntryId(null);
    }, [allowedSelectedRunId]);

    const interruptSelectedRun = useCallback(async () => {
        if (!allowedSelectedRunId || isInterrupting) {
            return;
        }

        setIsInterrupting(true);
        try {
            await apiClient.post({
                url: '/admin/agent/runs/{run_id}/interrupt',
                pathParams: { run_id: allowedSelectedRunId },
                mode: currentMode,
            });
            toast.success('Stop requested', { persistNotification: false });
            void mutateRun();
            runEventStream.refresh();
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Could not stop the run',
                { persistNotification: false },
            );
        } finally {
            setIsInterrupting(false);
        }
    }, [
        allowedSelectedRunId,
        apiClient,
        currentMode,
        isInterrupting,
        mutateRun,
        runEventStream,
    ]);

    const decideToolCall = useCallback(
        async (
            target: ToolApprovalDecisionTarget,
            decision: ToolApprovalDecision,
        ) => {
            const callIds = Array.isArray(target) ? [...target] : [target];
            if (
                !allowedSelectedRunId ||
                callIds.length === 0 ||
                callIds.some((callId) => decidingCallIds[callId])
            ) {
                return;
            }

            const operationKey = `${callIds.join(',')}:${decision}`;
            pendingToolDecisionOperationIdsRef.current[operationKey] ??=
                generateUuid();
            const operationId =
                pendingToolDecisionOperationIdsRef.current[operationKey];

            setDecidingCallIds((current) => ({
                ...current,
                ...Object.fromEntries(callIds.map((callId) => [callId, true])),
            }));
            try {
                await apiClient.post({
                    url: '/admin/agent/runs/{run_id}/tool-decisions',
                    pathParams: { run_id: allowedSelectedRunId },
                    body:
                        callIds.length === 1
                            ? {
                                  call_id: callIds[0],
                                  decision,
                              }
                            : {
                                  call_ids: callIds,
                                  decision,
                              },
                    requestId: operationId,
                    mode: currentMode,
                });
                delete pendingToolDecisionOperationIdsRef.current[operationKey];
                runEventStream.refresh();
                void mutateRun();
            } catch (error) {
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to submit tool approval decision.',
                    { persistNotification: false },
                );
                throw error;
            } finally {
                setDecidingCallIds((current) => {
                    const next = { ...current };
                    for (const callId of callIds) {
                        delete next[callId];
                    }
                    return next;
                });
            }
        },
        [
            allowedSelectedRunId,
            apiClient,
            currentMode,
            decidingCallIds,
            mutateRun,
            runEventStream,
        ],
    );

    return {
        decidingCallIds,
        decideToolCall,
        inspectedToolEntryId,
        interruptSelectedRun,
        isInterrupting,
        setInspectedToolEntryId,
    };
}
